"use client";

import { createContext, type ReactElement, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

/** A question for the chat, from „KI fragen“ in the top bar or the search window. */
export interface HelpAskRequest {
  readonly id: number;
  readonly question: string;
}

interface HelpChromeValue {
  readonly searchOpen: boolean;
  readonly setSearchOpen: (open: boolean) => void;
  readonly askRequest: HelpAskRequest | null;
  readonly ask: (question: string) => void;
  readonly clearAsk: () => void;
  /** The org's chat is on for visitors; „KI fragen“ shows only then. */
  readonly chatAvailable: boolean;
  readonly setChatAvailable: (available: boolean) => void;
}

const HelpChromeContext = createContext<HelpChromeValue | null>(null);

/** Shared state of the help center's top bar, search window and chat bubble. */
export function HelpChromeProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [askRequest, setAskRequest] = useState<HelpAskRequest | null>(null);
  const [chatAvailable, setChatAvailable] = useState<boolean>(false);
  const ask = useCallback((question: string): void => {
    setSearchOpen(false);
    setAskRequest((prev) => ({ id: (prev?.id ?? 0) + 1, question: question.trim() }));
  }, []);
  const clearAsk = useCallback((): void => setAskRequest(null), []);
  const value = useMemo(
    (): HelpChromeValue => ({ searchOpen, setSearchOpen, askRequest, ask, clearAsk, chatAvailable, setChatAvailable }),
    [ask, askRequest, chatAvailable, clearAsk, searchOpen],
  );
  return <HelpChromeContext.Provider value={value}>{children}</HelpChromeContext.Provider>;
}

export function useHelpChrome(): HelpChromeValue {
  const value = useContext(HelpChromeContext);
  if (value === null) throw new Error("useHelpChrome outside HelpChromeProvider");
  return value;
}
